import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule, RouterOutlet } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { FuseAlertComponent } from '@fuse/components/alert';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FooterDisclaimerComponent } from '@project/application-version/application-version.component';
import { HeaderComponent } from '@project/header/header.component';
import { SharedCommands, SnackbarCommand, SnackbarProperties } from '@project/shared.commands';
import { ImpersonationService } from 'app/core/auth/impersonation.service';
import { clearStoredTokens } from 'app/core/auth/session-expired';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguageInitializationService } from 'app/core/services/language-initialization.service';
import { UserService } from 'app/modules/admin/services/user.service';
import { environment } from 'environment';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { Subject, filter, pipe, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'app-classic-layout',
    templateUrl: './classic.component.html',
    styleUrls: ['./classic.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [
        CommonModule,
        FooterDisclaimerComponent,
        FuseAlertComponent,
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        HeaderComponent,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
        RouterOutlet,
        TranslocoModule,
        RouterModule,
        MatTooltipModule,
    ],
    animations: [fuseAnimations],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall!: boolean;
    isScreenMedium!: boolean;
    isScreenLarge!: boolean;

    navigation!: Navigation;
    private _unsubscribeAll: Subject<void> = new Subject<void>();
    snackbarProps: SnackbarProperties[] = [];

    profile!: KeycloakProfile;

    /**
     * Constructor
     */
    constructor(
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _keycloakService: KeycloakService,
        private _navigationService: NavigationService,
        private _translocoService: TranslocoService,
        private _languageService: LanguageInitializationService,
        protected userService: UserService,
        protected impersonationService: ImpersonationService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Getter for the QA environment flag
     */
    get isQaEnvironment(): boolean {
        return environment.environmentName === 'qa';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._keycloakService
            .loadUserProfile()
            .then((profile) => {
                this.profile = profile;
            })
            .catch(() => {
                // Fallback to token data if /account endpoint returns 403
                const token = this._keycloakService.getKeycloakInstance()?.tokenParsed;
                if (token) {
                    this.profile = {
                        firstName: token['given_name'],
                        lastName: token['family_name'],
                        email: token['email'],
                        username: token['preferred_username'],
                    } as KeycloakProfile;
                }
            });

        SharedCommands.onShowSnackBar
            .pipe(
                filter((snackbar) => !!snackbar && !!snackbar.uniqueId && (!!snackbar.message || !!snackbar.title)),
                pipe(takeUntil(this._unsubscribeAll)),
                tap((snackbar) => {
                    this.snackbarProps.push(snackbar);

                    if (this.snackbarProps.length > 4) {
                        this.snackbarProps.shift();
                    }

                    setTimeout(() => {
                        // Remove the snackbar with same id
                        const index = this.snackbarProps.findIndex((s) => s.uniqueId === snackbar.uniqueId);
                        if (index !== -1) {
                            this.snackbarProps.splice(index, 1);
                        }
                        SharedCommands.showSnackBar(null as unknown as SnackbarCommand);
                    }, snackbar.duration || 3000);
                })
            )
            .subscribe();

        // Subscribe to navigation data (already filtered by permissions in NavigationService)
        this._navigationService.navigation$.pipe(takeUntil(this._unsubscribeAll)).subscribe((navigation: Navigation) => {
            this.navigation = navigation;
        });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$.pipe(takeUntil(this._unsubscribeAll)).subscribe(({ matchingAliases }) => {
            // Check if the screen is small
            this.isScreenSmall = !matchingAliases.includes('md');
            this.isScreenMedium = !matchingAliases.includes('lg') && !this.isScreenSmall;
            this.isScreenLarge = !matchingAliases.includes('xl') && !this.isScreenMedium && !this.isScreenSmall;
        });
    }

    openKioskMode(): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>('mainNavigation');

        if (this.isKioskMode) {
            localStorage.setItem('kioskMode', 'false');
        } else {
            if (navigation) {
                // Toggle the opened status
                navigation.toggle();
            }

            localStorage.setItem('kioskMode', 'true');
        }
    }

    get isKioskMode(): boolean {
        return localStorage.getItem('kioskMode') === 'true';
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    openAccountManagement(): void {
        // Get Keycloak instance and server URL
        const keycloak = this._keycloakService.getKeycloakInstance();
        const keycloakBaseUrl = keycloak.authServerUrl;
        const realm = keycloak.realm;
        const clientId = keycloak.clientId;

        // Build the redirect URI (current application URL)
        const redirectUri = encodeURIComponent(window.location.origin);

        // Create the URL with the kc_action parameter for updating the profile
        const updateProfileUrl =
            `${keycloakBaseUrl}realms/${realm}/protocol/openid-connect/auth` +
            `?client_id=${clientId}` +
            `&redirect_uri=${redirectUri}` +
            `&response_type=code` +
            `&scope=openid+profile` +
            `&kc_action=UPDATE_PROFILE`;

        // Open in a new tab
        window.open(updateProfileUrl, '_self');
    }

    passwordChange(): void {
        // Get Keycloak instance and server URL
        const keycloak = this._keycloakService.getKeycloakInstance();
        const keycloakBaseUrl = keycloak.authServerUrl;
        const realm = keycloak.realm;
        const clientId = keycloak.clientId;

        // Build the redirect URI (current application URL)
        const redirectUri = encodeURIComponent(window.location.origin);

        // Create the URL with the kc_action parameter for updating the profile
        const updateProfileUrl =
            `${keycloakBaseUrl}realms/${realm}/protocol/openid-connect/auth` +
            `?client_id=${clientId}` +
            `&redirect_uri=${redirectUri}` +
            `&response_type=code` +
            `&scope=openid+profile` +
            `&kc_action=UPDATE_PASSWORD`;

        // Open in a new tab
        window.open(updateProfileUrl, '_self');
    }

    logout(): void {
        clearStoredTokens();
        this._keycloakService.logout();
    }

    stopImpersonation(): void {
        this.impersonationService.stopImpersonation();
    }

    get currentLang(): string {
        return this._translocoService.getActiveLang();
    }

    switchLanguage(): void {
        const newLang = this.currentLang === 'it' ? 'en' : 'it';
        this._languageService.switchLanguage(newLang);
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // |
    // | Snackbars
    // |
    // -----------------------------------------------------------------------------------------------------

    dismissed(snackbarId: string): void {
        const index = this.snackbarProps.findIndex((s) => s.uniqueId === snackbarId);
        if (index !== -1) {
            this.snackbarProps.splice(index, 1);
        }
    }

    calculateTopSpace(index: number): string {
        // I have to get all the elements previouse to this index and caluclate their height. Add a gfap between them of 8px nad return the total height
        // the id of html elements is 'snackbar' + index
        let totalHeight = 0;
        for (let i = 0; i < index; i++) {
            const element = document.getElementById('snackbar-' + i);
            if (element) {
                totalHeight += element.clientHeight + 8;
            }
        }
        return `top: ${totalHeight}px`;
    }
}
